import { createStore } from "./hooks.js";

/** Install-to-home-screen prompt state (Chrome/Edge/Android give us beforeinstallprompt; iOS needs instructions). */
interface BIPEvent extends Event { prompt(): Promise<void>; userChoice: Promise<{ outcome: "accepted" | "dismissed" }> }

const KEY = "todoer.installDismissed";
export const install = createStore<{ event: BIPEvent | null; dismissed: boolean }>({
  event: null,
  dismissed: (() => { try { return localStorage.getItem(KEY) === "1"; } catch { return false; } })(),
});

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => { e.preventDefault(); install.set((s) => ({ ...s, event: e as BIPEvent })); });
  window.addEventListener("appinstalled", () => install.set((s) => ({ ...s, event: null, dismissed: true })));
}

export const isStandalone = () => typeof window !== "undefined" && (window.matchMedia?.("(display-mode: standalone)")?.matches || (navigator as unknown as { standalone?: boolean }).standalone === true);
export const isIOS = () => typeof navigator !== "undefined" && /iPad|iPhone|iPod/.test(navigator.userAgent) && !/CriOS|FxiOS|EdgiOS/.test(navigator.userAgent);

export function dismissInstall(): void {
  try { localStorage.setItem(KEY, "1"); } catch { /* ignore */ }
  install.set((s) => ({ ...s, dismissed: true }));
}

export async function promptInstall(): Promise<void> {
  const e = install.get().event;
  if (!e) return;
  await e.prompt();
  const choice = await e.userChoice;
  install.set((s) => ({ ...s, event: null, dismissed: choice.outcome === "accepted" ? true : s.dismissed }));
}

/** How this browser lets people install the app. */
export type InstallPlatform =
  | "ios-safari" | "ios-other" | "android" | "desktop-chromium" | "mac-safari" | "firefox" | "other";

export function installPlatform(ua = typeof navigator !== "undefined" ? navigator.userAgent : ""): InstallPlatform {
  const iOS = /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && typeof navigator !== "undefined" && navigator.maxTouchPoints > 1);
  if (iOS) return /CriOS|FxiOS|EdgiOS|OPiOS/.test(ua) ? "ios-other" : "ios-safari";
  if (/Android/.test(ua)) return "android";
  if (/Firefox\//.test(ua)) return "firefox";
  if (/Edg\/|Chrome\//.test(ua)) return "desktop-chromium";
  if (/Macintosh/.test(ua) && /Safari\//.test(ua)) return "mac-safari";
  return "other";
}

/** Opens the install guide from anywhere (rendered once in the layout). */
export const openInstallGuide = () => window.dispatchEvent(new Event("todoer:install-guide"));

/** Installs straight away when the browser allows it, otherwise shows the steps. */
export async function startInstall(): Promise<void> {
  if (install.get().event) await promptInstall();
  else openInstallGuide();
}
